import React, { useState, useEffect, useCallback } from "react";
import { Gift, ArrowRight, X } from "lucide-react";
import { Button } from "./ui/button";
import LeadMagnetModal from "./LeadMagnetModal";

const STORAGE_KEY = "acdt_leadmagnet_dismissed_v1";
const SCROLL_TRIGGER = 0.55; // 55% della pagina

export default function LeadMagnetTrigger() {
  const [modalOpen, setModalOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") setDismissed(true);
    } catch (_) {}
  }, []);

  const onScroll = useCallback(() => {
    if (dismissed || bannerVisible) return;
    const scrollY = window.scrollY;
    const total = document.documentElement.scrollHeight - window.innerHeight;
    if (total <= 0) return;
    const ratio = scrollY / total;
    if (ratio >= SCROLL_TRIGGER) setBannerVisible(true);
  }, [dismissed, bannerVisible]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const dismiss = () => {
    setBannerVisible(false);
    setDismissed(true);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) {}
  };

  return (
    <>
      {/* Sticky bottom banner (scroll-triggered) */}
      {bannerVisible && !modalOpen && (
        <div
          data-testid="lead-magnet-banner"
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-40 rounded-2xl shadow-2xl bg-gradient-to-br from-[#0a1f55] to-[#102a6b] border border-[#f59e0b]/30 overflow-hidden animate-in slide-in-from-bottom-4"
        >
          <button
            onClick={dismiss}
            data-testid="lead-magnet-banner-dismiss"
            className="absolute top-3 right-3 h-7 w-7 rounded-full bg-white/10 hover:bg-white/20 text-white/80 flex items-center justify-center"
            aria-label="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="p-5 md:p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center flex-shrink-0">
                <Gift className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[10.5px] font-extrabold tracking-[0.22em] uppercase text-[#fbbf24]">
                  Guida gratis · PDF
                </div>
                <div className="text-white font-extrabold text-base leading-tight">
                  Le 7 competenze digitali 2026
                </div>
              </div>
            </div>
            <p className="text-slate-200/90 text-[13px] leading-relaxed mb-4">
              Le competenze più richieste dal mercato per generare reddito extra. Scaricalo in un clic.
            </p>
            <Button
              onClick={() => { setModalOpen(true); setBannerVisible(false); }}
              data-testid="lead-magnet-banner-cta"
              className="w-full bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold h-10"
            >
              Scarica gratis
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Inline CTA sezione (mostrata sempre in Home) */}
      <section
        id="lead-magnet"
        data-testid="lead-magnet-section"
        className="relative py-16 md:py-20 bg-gradient-to-br from-[#fef3c7] via-white to-[#fef3c7]/40"
      >
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-12 gap-8 items-center rounded-3xl bg-white border-2 border-[#f59e0b]/30 shadow-xl p-8 md:p-10">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b]/15 border border-[#f59e0b]/40">
                <Gift className="h-3.5 w-3.5 text-[#f59e0b]" />
                <span className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#0a1f55]">
                  Regalo · PDF gratuito
                </span>
              </div>
              <h2 className="mt-4 text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#0a1f55] leading-tight">
                Scarica gratis: <span className="text-[#f59e0b]">Le 7 competenze digitali del 2026</span>
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                La guida che ti mostra le competenze più richieste dal mercato e come trasformarle
                in reddito. 15 minuti di lettura, valore immediato.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-700">
                <li>✓ Le competenze più pagate nel 2026</li>
                <li>✓ Come integrare AI, tecnologia e sostenibilità</li>
                <li>✓ Prossimi passi per certificarti come Consulente Digitale</li>
              </ul>
            </div>
            <div className="md:col-span-5 flex justify-center md:justify-end">
              <Button
                onClick={() => setModalOpen(true)}
                data-testid="lead-magnet-inline-cta"
                className="bg-[#0a1f55] hover:bg-[#102a6b] text-white font-extrabold h-14 px-8 text-base shadow-lg"
              >
                <Gift className="mr-2 h-5 w-5" />
                Scarica gratis il PDF
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LeadMagnetModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fonte={bannerVisible ? "banner" : "home"}
      />
    </>
  );
}
